import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Card,
  IconButton,
  Stack,
  Fade,
  TextField,
  Typography,
} from "@mui/material";
import { MessageSquare, X, Send, Minimize2 } from "lucide-react";
import axios from "axios";
import { endpoint } from "../../resource/Constant";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "motion/react"
import { P } from "framer-motion/dist/types.d-DagZKalS";
import { Grow } from "@mui/material";
import { Slide } from "@mui/material";


const DEFAULT_HEADER_GRADIENT = "linear-gradient(90deg, #1976d2, #42a5f5, #66bb6a)";
const DEFAULT_USER_GRADIENT = "linear-gradient(135deg, #1976d2, #42a5f5, #66bb6a)";
const THEME_EVENT = "crawling-theme-updated";

interface Message {
  id: string;
  role: "user" | "bot";
  content: string;
  timestamp: Date;
  isLoader? : boolean;
}

interface UserFormData {
  name: string;
  email: string;
  phone: string;
}

export const EmbedChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const {useremail} = useParams();
  const [headerGradient, setHeaderGradient] = useState<string>(DEFAULT_HEADER_GRADIENT);
  const [userGradient, setUserGradient] = useState<string>(DEFAULT_USER_GRADIENT);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      role: "bot",
      content: "Hi! I'm here to help you with any questions about the website. What would you like to know?",
      timestamp: new Date(),
    },
  ]);
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  
  // User authentication states
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
  const [showUserForm, setShowUserForm] = useState(true); // Start as true to show form until validated
  const [isValidating, setIsValidating] = useState(true); // Track validation in progress
  const [formData, setFormData] = useState<UserFormData>({ name: "", email: "", phone: "" });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState("");

  const buildGradient = (angle, colors) =>
    `linear-gradient(${angle}deg, ${colors})`

  // Function to get cookie value
  const getCookieValue = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
    return null;
  };

  // Function to set cookie
  const setCookie = (name: string, value: string, days: number = 7) => {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/`;
  };

  // Function to validate and check user token
  const validateUserToken = async () => {
    try {
      setIsValidating(true);
      const token = getCookieValue("website-chatbot-user-token");
      
      console.log("Token found:", !!token);
      
      if (!token) {
        console.log("No token found, showing form");
        setShowUserForm(true);
        setIsUserAuthenticated(false);
        setIsValidating(false);
        return;
      }

      // Call API to validate token
      const response = await axios.get(`${endpoint}/api/crawling/validate-user-token`, {
        params: { token }
      });

      console.log("Validation response status:", response?.status);
      console.log("Validation response data:", response?.data);

      // If we get a 200 status, consider the token valid
      if (response?.status === 200) {
        console.log("Token is valid (200 status), authenticated");
        setIsUserAuthenticated(true);
        setShowUserForm(false);
      }
      else {
        console.log("Token is invalid, showing form");
        setShowUserForm(true);
        setIsUserAuthenticated(false);
      }
    } catch (error: any) {
      console.error("Token validation error:", error);
      setShowUserForm(true);
      setIsUserAuthenticated(false);
    } finally {
      setIsValidating(false);
    }
  };

  // Function to handle form submission
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError("");

    try {
      const response = await axios.post(
        `${endpoint}/api/crawling/add-website-chatbot-user`,
        {
          user_name: formData.name,
          user_email: formData.email,
          user_mobile_number: formData.phone,
        }, {
          params : {
            user_email : useremail
          }
        }
      );
      if (response?.data?.data?.token) {
        const token = response.data.data.token;
        setCookie("website-chatbot-user-token", token, 30);
        setIsUserAuthenticated(true);
        setShowUserForm(false);
        setFormData({ name: "", email: "", phone: "" });
      } else {
        setFormError("Failed to register. Please try again.");
      }
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Error registering user. Please try again.";
      setFormError(errorMsg);
    } finally {
      setFormLoading(false);
    }
  };

  useEffect(() => {
  messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Check for existing token on component mount
  useEffect(() => {
    validateUserToken();
  }, []);

  // Validate user token when chatbot opens
  useEffect(() => {
    if (isOpen) {
      validateUserToken();
    }
  }, [isOpen]);

  useEffect(() => {
    if (localStorage.getItem("crawling-gradient-header") || localStorage.getItem("crawling-gradient-bubble")) {
      const loadGradients = () => {
        const storedHeader = localStorage.getItem("crawling-gradient-header");
        const storedBubble = localStorage.getItem("crawling-gradient-bubble");
        if (storedHeader) setHeaderGradient(storedHeader);
        if (storedBubble) setUserGradient(storedBubble);
      };
      loadGradients();
      // Update if localStorage changes in other tabs
      window.addEventListener("storage", loadGradients);
      // Update live in this tab via custom event
      const handleThemeEvent = (e: Event) => {
        const detail = (e as CustomEvent)?.detail;
        if (!detail) return;
        if (detail.headerGradient) setHeaderGradient(detail.headerGradient);
        if (detail.bubbleGradient) setUserGradient(detail.bubbleGradient);
      };
      window.addEventListener(THEME_EVENT, handleThemeEvent);
      return () => {
        window.removeEventListener("storage", loadGradients);
        window.removeEventListener(THEME_EVENT, handleThemeEvent);
      };
    }
    else {
      axios.get(`${endpoint}/api/crawling/get-color-public` ,{params : { user_email : useremail }})
      .then((response) => {
        if (response?.data?.data?.header_gradient) {
          const header_gradient = response?.data?.data?.header_gradient;
          setHeaderGradient(buildGradient(header_gradient.angle, header_gradient.color))

        }
        if (response?.data?.data?.user_gradient) {
          const user_gradient = response?.data?.data?.user_gradient;
          setUserGradient(buildGradient(user_gradient.angle, user_gradient.color))
        }

      })
    }
  }, []);

  // Make the iframe transparent and ensure proper positioning
  useEffect(() => {
    document.body.style.background = 'transparent';
    document.body.style.backgroundColor = 'transparent';
    document.documentElement.style.background = 'transparent';
    document.documentElement.style.backgroundColor = 'transparent';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100vh';
    document.body.style.width = '100vw';
    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';
    document.documentElement.style.height = '100%';
    document.documentElement.style.width = '100%';
    
    // Also set the root element background
    const root = document.getElementById('root');
    if (root) {
      root.style.background = 'transparent';
      root.style.backgroundColor = 'transparent';
      root.style.height = '100%';
      root.style.width = '100%';
    }
    
    return () => {
      document.body.style.background = '';
      document.body.style.backgroundColor = '';
      document.documentElement.style.background = '';
      document.documentElement.style.backgroundColor = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.body.style.width = '';
      document.documentElement.style.margin = '';
      document.documentElement.style.padding = '';
      document.documentElement.style.height = '';
      document.documentElement.style.width = '';
      if (root) {
        root.style.background = '';
        root.style.backgroundColor = '';
        root.style.height = '';
        root.style.width = '';
      }
    };
  }, []);

  // Notify parent window to resize iframe based on chat state
  useEffect(() => {
    if (window.parent !== window) {
      const payload =
        isOpen && !isMinimized
          ? { type: "chatbot-resize", open: true, width: 350, height: 540 }
          : { type: "chatbot-resize", open: false, width: 96, height: 96 };
      window.parent.postMessage(payload, "*");
    }
  }, [isOpen, isMinimized]);

  // Send initial size on mount to ensure parent sets default iframe size
  useEffect(() => {
    if (window.parent !== window) {
      window.parent.postMessage({ type: "chatbot-resize", open: false, width: 96, height: 96 }, "*");
      window.parent.postMessage({ type: "chatbot-ready" }, "*");
    }
  }, []);

  const handleSend = async () => {
    if (!input.trim()) return;
    
    // Only allow sending if user is authenticated
    if (!isUserAuthenticated) {
      setFormError("Please fill out the form first");
      return;
    }

    const messageText = input;
    setInput("");


    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: messageText,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);

      // 3. Add loader message
    const loaderMessage: Message = {
      id: "loader",
      role: "bot",
      content: "",    // replace this with your animation component
      isLoader: true,          // custom flag
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, loaderMessage]);

      try {

      const response = await axios.post(`${endpoint}/api/crawling/embed-chat`, {
        query: messageText,
      }, {
        params : {
          user_email : useremail,
          token : getCookieValue("website-chatbot-user-token") || "",
        }
      });

      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "bot",
        content: response?.data?.data?.answer || "No response",
        timestamp: new Date(),
      };

      // 5. Remove loader and add bot message
      setMessages((prev) =>
        prev.filter((msg) => msg.id !== "loader").concat(botMessage)
      );

    } catch (error: any) {
      // Remove loader and show error
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.detail || 
                          "Error: Unable to reach server.";
      
      setMessages((prev) =>
        prev.filter((msg) => msg.id !== "loader").concat({
          id: (Date.now() + 2).toString(),
          role: "bot",
          content: errorMessage,
          timestamp: new Date(),
        })
      );
    }
  };

  if (!isOpen) {
    return (
      <Box
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "flex-end",
          background: "transparent",
          pointerEvents: "none",
          width: 96,
          height: 96,
          zIndex: 999999,
        }}
      >
        <Button
          onClick={() => {
            setIsOpen(true);
            // Immediately notify parent to resize
            if (window.parent !== window) {
              window.parent.postMessage({ type: "chatbot-resize", open: true, width: 350, height: 540 }, "*");
            }
          }}
          variant="contained"
          sx={{
            pointerEvents: "auto",
            height: 64,
            width: 64,
            minWidth: 64,
            maxWidth: 64,
            borderRadius: "50%",
            position: "relative",
            background: userGradient,
            // border: "2px solid rgba(255,255,255,0.3)",
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&:hover": {
              background: userGradient,
              transform: "scale(1.05)",
            },
            transition: "all 0.3s ease",
          }}
        >
          <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <MessageSquare size={33} color="white" />
            <Box
              sx={{
                position: "absolute",
                top: -6,
                right: -6,
                width: 12,
                height: 12,
                bgcolor: "success.main",
                borderRadius: "50%",
                border: "2px solid white",
                animation: "pulse 2s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.5 },
                },
              }}
            />
          </Box>
        </Button>
      </Box>
    );
  }

  return (
    <Slide 
    direction="up"
    in={!isMinimized}
    timeout={{ enter: 400, exit: 250 }}
    easing={{
      enter: "cubic-bezier(0.22, 1, 0.36, 1)", // smooth ease-out
      exit: "ease-in",
    }}
    mountOnEnter
    >
      <Fade
      in={!isMinimized} timeout={300}
      >
    <Box
      sx={{
        position: "fixed",
        bottom: 16,
        right: 16,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "flex-end",
        background: "transparent",
        pointerEvents: "none",
        width: isMinimized ? 96 : 350,
        height: isMinimized ? 96 : 540,
      }}
    >
      <Box sx={{ zIndex: 1300, pointerEvents: "auto", width: "100%", height: "100%" }}>
        <Card
          sx={{
            width: "100%",
            height: "100%",
            transition: "all 0.5s ease",
            borderRadius : 6,
            overflow: "hidden",
            transformOrigin: "bottom right",
            background: "linear-gradient(135deg, #ffffff, #f8fbff)",
            backdropFilter: "blur(14px)",
            border: "1px solid rgba(0,0,0,0.08)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              borderBottom: "1px solid rgba(25,118,210,0.3)",
              background: headerGradient,
              overflow: "hidden",
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: "50%",
                  bgcolor: "rgba(255,255,255,0.3)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                <MessageSquare size={25} color="white" />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "white", fontSize: "1rem" }}>
                  AI Assistant
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", fontSize: "0.65rem", fontWeight: 500 }}>
                  Online • Ready to help
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.5} sx={{ position: "relative", zIndex: 1 }}>
              {/* <IconButton
                size="small"
                onClick={() => {
                  const newMinimized = !isMinimized;
                  setIsMinimized(newMinimized);
                  // Immediately notify parent to resize
                  if (window.parent !== window) {
                    if (newMinimized) {
                      window.parent.postMessage({ type: "chatbot-resize", open: false, width: 96, height: 96 }, "*");
                    } else {
                      window.parent.postMessage({ type: "chatbot-resize", open: true, width: 350, height: 540 }, "*");
                    }
                  }
                }}
                sx={{
                  color: "white",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.2)",
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <Minimize2 size={16} />
              </IconButton> */}
              <IconButton
                size="small"
                onClick={() => {
                  setIsOpen(false);
                  // Immediately notify parent to resize
                  if (window.parent !== window) {
                    window.parent.postMessage({ type: "chatbot-resize", open: false, width: 96, height: 96 }, "*");
                  }
                }}
                sx={{
                  color: "white",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.2)",
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <X size={16} />
              </IconButton>
            </Stack>
          </Box>

          {!isMinimized && (
            <>
              {/* Show form if validating or if user form should be shown */}
              {isValidating || showUserForm ? (
                <Box
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    p: 2,
                    height: 480,
                    background: "linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.05))",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {isValidating ? (
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                        Loading...
                      </Typography>
                    </Box>
                  ) : (
                  <Box
                    component="form"
                    onSubmit={handleFormSubmit}
                    sx={{
                      width: "100%",
                      maxWidth: 300,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        color: "text.primary",
                        textAlign: "center",
                        mb: 1,
                      }}
                    >
                      Welcome! 👋
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        textAlign: "center",
                        mb: 1,
                        fontSize: "0.875rem",
                      }}
                    >
                      Please provide your details to get started
                    </Typography>

                    {formError && (
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: "error.light",
                          color: "error.dark",
                          borderRadius: 2,
                          fontSize: "0.875rem",
                          textAlign: "center",
                        }}
                      >
                        {formError}
                      </Box>
                    )}

                    <TextField
                      fullWidth
                      label="Name"
                      size="small"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      required
                      disabled={formLoading}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "0.875rem",
                          "& fieldset": {
                            borderColor: "rgba(25, 118, 210, 0.3)",
                          },
                          "&:hover fieldset": {
                            borderColor: "rgba(25, 118, 210, 0.4)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "rgba(25, 118, 210, 0.6)",
                          },
                        },
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      size="small"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      required
                      disabled={formLoading}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "0.875rem",
                          "& fieldset": {
                            borderColor: "rgba(25, 118, 210, 0.3)",
                          },
                          "&:hover fieldset": {
                            borderColor: "rgba(25, 118, 210, 0.4)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "rgba(25, 118, 210, 0.6)",
                          },
                        },
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Phone"
                      type="tel"
                      size="small"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      disabled={formLoading}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "0.875rem",
                          "& fieldset": {
                            borderColor: "rgba(25, 118, 210, 0.3)",
                          },
                          "&:hover fieldset": {
                            borderColor: "rgba(25, 118, 210, 0.4)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "rgba(25, 118, 210, 0.6)",
                          },
                        },
                      }}
                    />

                    <Button
                      fullWidth
                      variant="contained"
                      type="submit"
                      disabled={
                        formLoading ||
                        !formData.name || 
                        !formData.email
                      }
                      sx={{
                        background: userGradient,
                        mt: 1,
                        fontWeight: "bold",
                        "&:hover": {
                          background: userGradient,
                          transform: "scale(1.02)",
                          boxShadow: "0 0 30px rgba(147,51,234,0.6)",
                        },
                        "&:disabled": {
                          opacity: 0.5,
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      {formLoading ? "Processing..." : "Get Started"}
                    </Button>
                  </Box>
                  )}
                </Box>
              ) : (
                <>
                  {/* Messages */}
                  <Box
                    sx={{
                      flex: 1,
                      overflowY: "auto",
                      p: 2,
                      height: 480,
                      background: "linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.05))",
                    }}
                  >
                    <Stack spacing={2}>
                  {messages.map((message) => (
                    <Fade in key={message.id} timeout={500}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: message.role === "user" ? "flex-end" : "flex-start",
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: "85%",
                            borderRadius: 4,
                            px: 2.5,
                            py: 1.5,
                            boxShadow: 3,
                            ...(message.role === "user"
                              ? {
                                  background: headerGradient,
                                  color: "white",
                                  borderTopRightRadius: 4,
                                  borderBottomRightRadius: 8,
                                }
                              : {
                                  bgcolor: "background.paper",
                                  color: "text.primary",
                                  border: "1px solid rgba(25, 118, 210, 0.1)",
                                  borderTopLeftRadius: 4,
                                  borderBottomLeftRadius: 8,
                                }),
                          }}
                        >
                          {/* Loader */}
                          {message.isLoader ? (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              {[0, 1, 2].map((i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    bgcolor: "rgba(0,0,0,0.3)",
                                    animation: "bounce 1.4s infinite",
                                    animationDelay: `${i * 0.15}s`,
                                    "@keyframes bounce": {
                                      "0%, 80%, 100%": { transform: "translateY(0)" },
                                      "40%": { transform: "translateY(-10px)" },
                                    },
                                  }}
                                />
                              ))}
                            </Stack>
                          ) : (
                            <>
                              <Typography variant="body2" sx={{ lineHeight: 1.75 , color : message.role === 'user' ? "white" : 'inherit'}}>
                                {message.content}
                              </Typography>
                              {/* Time */}
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  opacity: 0.6,
                                  mt: 0.75,
                                  fontWeight: 500,
                                  color : message.role === 'user' ? 'white' : 'inherit'
                                }}
                              >
                                {message.timestamp.toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>
                    </Fade>
                  ))}
                </Stack>
                {/* Auto-scroll anchor */}
                <div ref={messagesEndRef} />
              </Box>

              {/* Input */}
              <Box
                sx={{
                  p: 2,
                  borderTop: "1px solid rgba(25,118,210,0.2)",
                  bgcolor: "rgba(255,255,255,0.95)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <Stack direction="row" spacing={1}>
                  <TextField
                    autoComplete="off"
                    fullWidth
                    size="small"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Ask me anything..."
                    onKeyDown={(e) => e.key === "Enter" && handleSend()}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "rgba(255,255,255,0.7)",
                        fontSize: "0.875rem",
                        "& fieldset": {
                          borderWidth: 2,
                          borderColor: "rgba(25, 118, 210, 0.3)",
                        },
                        "&:hover fieldset": {
                          borderColor: "rgba(25, 118, 210, 0.4)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "rgba(25, 118, 210, 0.6)",
                        },
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSend}
                    disabled={!input.trim()}
                    sx={{
                      background: userGradient,
                      minWidth: 48,
                      px: 2,
                      "&:hover": {
                        background: userGradient,
                        transform: "scale(1.05)",
                        boxShadow: "0 0 30px rgba(147,51,234,0.6)",
                      },
                      "&:disabled": {
                        opacity: 0.5,
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Send size={16} />
                  </Button>
                </Stack>
              </Box>
                </>
              )}
            </>
          )}
        </Card>
      </Box>
    </Box>
    </Fade>
    </Slide>
  );
};

export default EmbedChat;
